import { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Container, Paper } from '@mui/material';
import { Link as RouterLink, useRouteError, isRouteErrorResponse } from 'react-router-dom';
import { ApiError } from '@/api/errors';
import { redirectToCentralizedLogin } from '@/auth/silentRefresh';
import { useCopy } from '@/hooks/useCopy';

const REDIRECT_SECONDS = 3;

export function RouteError() {
  const err = useRouteError();
  const { t } = useCopy();

  let message: string;
  let requestId: string | null = null;

  if (err instanceof ApiError) {
    message = t(`errors.codes.${err.code}`, err.details.message ?? err.code);
    requestId = err.requestId ?? null;
  } else if (isRouteErrorResponse(err)) {
    message = `${err.status} ${err.statusText}`;
  } else if (err instanceof Error) {
    message = err.message;
  } else {
    message = t('errors.generic', 'Something went wrong.');
  }

  // A session-expired error auto-forwards to the centralized login (preserving
  // the deep link so re-login returns the operator here). All other route
  // errors (404, generic) keep the message + Home-button card below.
  const isSessionExpired = err instanceof ApiError && err.code === 'SESSION_EXPIRED';
  if (isSessionExpired) {
    return <SessionExpiredRedirect message={message} requestId={requestId} />;
  }

  return (
    <Container maxWidth="sm" sx={{ py: 8 }}>
      <Paper sx={{ p: 4 }} elevation={1}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {message}
          {requestId ? (
            <Box component="span" sx={{ display: 'block', fontSize: '0.75rem', mt: 0.5, opacity: 0.8 }}>
              {t('common.reference', 'Reference')}: {requestId}
            </Box>
          ) : null}
        </Alert>
        <Button component={RouterLink} to="/" variant="contained">
          {t('nav.home', 'Home')}
        </Button>
      </Paper>
    </Container>
  );
}

function SessionExpiredRedirect({
  message,
  requestId,
}: {
  message: string;
  requestId: string | null;
}) {
  const { t } = useCopy();
  const [seconds, setSeconds] = useState(REDIRECT_SECONDS);
  const redirected = useRef(false);
  // Capture the page the operator was on so re-login returns them here.
  const currentPath = useRef(
    typeof window !== 'undefined'
      ? window.location.pathname + window.location.search
      : '/',
  );

  function goNow() {
    if (redirected.current) return;
    redirected.current = true;
    redirectToCentralizedLogin(currentPath.current);
  }

  useEffect(() => {
    const id = setInterval(() => setSeconds((s) => (s <= 1 ? 0 : s - 1)), 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (seconds === 0) goNow();
  }, [seconds]);

  return (
    <Container maxWidth="sm" sx={{ py: 8 }}>
      <Paper sx={{ p: 4 }} elevation={1}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {message}
          {requestId ? (
            <Box component="span" sx={{ display: 'block', fontSize: '0.75rem', mt: 0.5, opacity: 0.8 }}>
              {t('common.reference', 'Reference')}: {requestId}
            </Box>
          ) : null}
        </Alert>
        <Box sx={{ mb: 2, color: 'text.secondary' }}>
          {t('auth.sessionExpired.redirecting', 'Redirecting to sign in… ({{seconds}})', { seconds })}
        </Box>
        <Button variant="contained" onClick={goNow}>
          {t('auth.sessionExpired.signInNow', 'Sign in now')}
        </Button>
      </Paper>
    </Container>
  );
}
